import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  Input,
  Stack,
  Table,
  Typography
} from "@mui/joy";
import { createRoute, useNavigate } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";
import { tenantService } from "../../shared/api/tenant.service";
import { useRoleBasedNavigation } from "../../shared/hook/useRoleBasedNavigation";
import { useOnboardingStore, useUserStore } from "../../shared/store";
import { OnboardingChevronStepper } from "../../shared/ui/OnboardingChevronStepper";
import { onboardingRootRoute } from "./onboarding-root";

interface WorkdayConfig {
  tenant: string;
}

/**
 * Extracts the tenant ID from a Workday URL.
 * Supports multiple URL formats:
 * - https://wd5.myworkday.com/<tenant_id>/d/home.htmld (standard home page)
 * - https://a.b.myworkday.com/wday/authgwy/<tenant_id>/upc/login (auth gateway)
 * Returns the tenant ID if found, or null if the input is not a valid Workday URL.
 */
const extractTenantIdFromUrl = (input: string): string | null => {
  // Check if the input looks like a URL (contains myworkday.com)
  if (!input.includes("myworkday.com")) {
    return null;
  }

  try {
    // Handle URLs that might not have a protocol
    const urlString = input.startsWith("http") ? input : `https://${input}`;
    const url = new URL(urlString);

    // Verify it's a myworkday.com domain
    if (!url.hostname.endsWith("myworkday.com")) {
      return null;
    }

    // Extract tenant ID from the path
    const pathParts = url.pathname.split("/").filter((part) => part.length > 0);

    if (pathParts.length === 0) {
      return null;
    }

    const isValidTenantId = (id: string) => /^[a-zA-Z0-9_-]+$/.test(id);

    // Check for auth gateway format: /wday/authgwy/<tenant_id>/...
    if (
      pathParts.length >= 3 &&
      pathParts[0] === "wday" &&
      pathParts[1] === "authgwy"
    ) {
      return isValidTenantId(pathParts[2]) ? pathParts[2] : null;
    }

    // Standard format: /<tenant_id>/d/home.htmld or /<tenant_id>/...
    // The first path segment is the tenant ID
    return isValidTenantId(pathParts[0]) ? pathParts[0] : null;
  } catch {
    return null;
  }
};

const WorkdayPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    uuid,
    setTenantId,
    markStageComplete: markStageCompleteInStore
  } = useOnboardingStore();
  const user = useUserStore((state) => state.user);
  const {
    getNext,
    accessiblePages,
    roles,
    isWorkdayAdmin,
    rolesLoaded,
    getStartingPage
  } = useRoleBasedNavigation();

  // Redirect users without workday-admin role to their appropriate page
  // Guard against empty roles to avoid premature redirect when roles are stale from persisted store
  useEffect(() => {
    if (rolesLoaded && roles.length > 0 && !isWorkdayAdmin) {
      const startPage = getStartingPage();
      navigate({ to: startPage });
    }
  }, [rolesLoaded, roles, isWorkdayAdmin, navigate, getStartingPage]);

  // Determine the next page label based on user's accessible pages
  const getNextButtonLabel = () => {
    const nextPath = getNext("/onboarding/workday");
    if (!nextPath) return "Complete";

    // Find the page config for the next path
    const nextPage = accessiblePages.find((p) => p.path === nextPath);
    return nextPage?.label || "Next";
  };
  const [workdayConfig, setWorkdayConfig] = useState<WorkdayConfig>({
    tenant: ""
  });
  const [workdayUrl, setWorkdayUrl] = useState<string>("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExistingData, setIsLoadingExistingData] = useState(true);
  const [apiError, setApiError] = useState<string>("");
  const [infoMessage, setInfoMessage] = useState<string>("");
  const [isExistingTenant, setIsExistingTenant] = useState(false);

  // ARCHITECTURE: Load existing data from hub-api (MongoDB) on component mount
  // Do NOT use sessionStorage - MongoDB is the source of truth
  useEffect(() => {
    const loadExistingData = async () => {
      if (!uuid) {
        setIsLoadingExistingData(false);
        return;
      }

      try {
        const response = await tenantService.getOnboardingStatus(uuid);
        if (
          response.success &&
          response.data?.onboardingData?.workday?.tenant
        ) {
          const existingTenant = response.data.onboardingData.workday.tenant;
          setWorkdayConfig({ tenant: existingTenant });
          setWorkdayUrl(existingTenant);
          setIsExistingTenant(true);

          // Also update the store with tenantId if available
          if (response.data.tenantId) {
            setTenantId(response.data.tenantId);
          }
        }
      } catch {
        // If API call fails, continue with empty form - user can enter data
      } finally {
        setIsLoadingExistingData(false);
      }
    };

    loadExistingData();
  }, [uuid, setTenantId]);

  /**
   * Handles URL input changes. Attempts to extract tenant ID from Workday URLs.
   * If the input is a URL, extracts the tenant ID and stores both the URL and tenant.
   * If not a URL, treats the input as a direct tenant ID.
   */
  const handleUrlChange = (value: string) => {
    setWorkdayUrl(value);

    // Clear errors when user starts typing
    if (errors.tenant) {
      setErrors((prev) => ({
        ...prev,
        tenant: ""
      }));
    }

    // Try to extract tenant ID from URL
    const extractedTenantId = extractTenantIdFromUrl(value);

    if (extractedTenantId) {
      // Successfully extracted tenant ID from URL
      setWorkdayConfig((prev) => ({
        ...prev,
        tenant: extractedTenantId
      }));
    } else if (!value.includes("myworkday.com") && !value.includes("http")) {
      // Input doesn't look like a URL, treat it as a direct tenant ID
      setWorkdayConfig((prev) => ({
        ...prev,
        tenant: value
      }));
    } else {
      // Looks like a URL but couldn't extract tenant ID - clear tenant
      setWorkdayConfig((prev) => ({
        ...prev,
        tenant: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!workdayConfig.tenant.trim()) {
      newErrors.tenant = "Please enter a valid Workday URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError("");
    setInfoMessage("");

    try {
      // ARCHITECTURE: Do NOT use sessionStorage - MongoDB is the source of truth
      // The useOnboardingStore has tenantId if it was loaded from the API on mount

      // If tenant already exists (from previous onboarding), skip copyTenant0 and go to next page
      // The tenantId would have been loaded into the store during the useEffect on mount
      const { tenantId: existingTenantId } = useOnboardingStore.getState();
      if (isExistingTenant && existingTenantId) {
        // Navigate to the next page based on user roles
        const nextPage = getNext("/onboarding/workday");
        navigate({ to: nextPage || "/onboarding/completion" });
        return;
      }

      // Use the tenant ID from workday config as the client name
      const clientName = workdayConfig.tenant;

      // Prepare workday data for API and storage
      // Always use oauth and wql: true
      const workdayData = {
        tenant: workdayConfig.tenant,
        authMethod: "oauth" as "oauth" | "account-password",
        wql: true,
        timeFormat: "America/Los_Angeles", // Default timezone
        use_data_account_for_web_services: true,
        use_event_wid_from_workday_int: false,
        reports: {
          owner: "",
          questionnaires: "",
          roles: "",
          roleAssignmentChanges: "",
          account: "",
          password: ""
        },
        service_center: {
          account: "",
          password: "",
          oAuthClientID: "",
          oAuthRefreshToken: "",
          oAuthClientSecret: ""
        },
        shared_secret: "",
        encrypted: false,
        auto_book_step: "",
        availability_step: "",
        auto_book_stage: "",
        certificate: ""
      };

      // Ensure uuid is available - it's required for looking up the onboarding record
      if (!uuid) {
        setApiError(
          "Session error: Missing onboarding UUID. Please log in again."
        );
        setIsLoading(false);
        return;
      }

      // Call API to copy tenant0
      // The uuid is required to look up the onboarding record and determine the correct environment
      const apiResponse = await tenantService.copyTenant0({
        clientName: clientName,
        workdayConfig: workdayData,
        uuid: uuid
      });

      const tenantId = apiResponse?.client_id;
      if (!tenantId) {
        throw new Error("Invalid response from server: Missing client_id");
      }

      // Check if client already existed
      if (apiResponse.message?.includes("already exists")) {
        setInfoMessage(
          `Tenant "${tenantId}" already exists. You can continue with the configuration.`
        );
      }

      // Save tenantId to Zustand store for navigation state
      setTenantId(tenantId);

      // Update onboarding stage to WORKDAY_CONFIGURED
      // Note: workday_region, zoom_connected, target_environment are already in MongoDB
      // from the instructions page - we only add workday config here
      await tenantService.updateOnboardingStage(tenantId, {
        uuid: uuid,
        stage: "WORKDAY_CONFIGURED",
        tenantId: tenantId,
        onboardingData: { workday: workdayData }
      });

      // Mark workday stage as complete in the sequenced workflow
      // This will trigger domain/SSO emails to be sent to the appropriate roles
      try {
        const userEmail = user?.email || "unknown";
        await tenantService.markStageComplete(uuid, "workday", userEmail);
        // Also update local store
        markStageCompleteInStore("workday", userEmail);
      } catch {
        // Don't fail the navigation if stage marking fails
      }

      // Navigate to the next page based on user roles
      // If user only has workday-admin role, they may see a summary/completion page
      // If they also have it-admin or sso-admin, they continue to domain/sso
      const nextPage = getNext("/onboarding/workday");
      navigate({ to: nextPage || "/onboarding/completion" });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === "object" && error !== null && "message" in error
            ? String((error as { message: unknown }).message)
            : "Failed to create tenant. Please try again.";
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <OnboardingChevronStepper currentStep={1} />
      <Box sx={{ py: 4 }}>
        <Typography level="h2" sx={{ mb: 3, textAlign: "center" }}>
          Workday Connection Details
        </Typography>

        <Alert sx={{ mb: 3 }}>
          <Typography level="body-sm">
            Please provide your Workday tenant URL so your Rooster environment
            connects correctly.
          </Typography>
        </Alert>

        {apiError && (
          <Alert color="danger" sx={{ mb: 3 }}>
            {apiError}
          </Alert>
        )}

        {infoMessage && (
          <Alert color="warning" sx={{ mb: 3 }}>
            {infoMessage}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid xs={12} md={8}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography level="title-md" sx={{ mb: 3 }}>
                  Workday Tenant Information
                </Typography>

                <Table
                  variant="outlined"
                  sx={{
                    "& thead th:nth-of-type(1)": { width: "30%" },
                    "& thead th:nth-of-type(2)": { width: "70%" }
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography
                          level="body-md"
                          fontWeight="bold"
                          sx={{ textAlign: "left" }}
                        >
                          Workday URL
                        </Typography>
                      </td>
                      <td>
                        <FormControl
                          error={!!errors.tenant}
                          sx={{ width: "100%" }}
                        >
                          <Input
                            placeholder="Paste your Workday URL (e.g., https://wd5.myworkday.com/acme/d/home.htmld)"
                            value={workdayUrl}
                            onChange={(e) => handleUrlChange(e.target.value)}
                            size="sm"
                            disabled={isExistingTenant}
                          />
                          <FormHelperText>
                            Paste the homepage URL of the Workday tenant
                            associated with this Rooster environment. Workday
                            URL cannot be changed after your tenant is created.
                          </FormHelperText>
                          {errors.tenant && (
                            <FormHelperText sx={{ color: "danger.500" }}>
                              {errors.tenant}
                            </FormHelperText>
                          )}
                          {isExistingTenant && (
                            <FormHelperText>
                              Workday URL cannot be changed after your tenant is
                              created
                            </FormHelperText>
                          )}
                        </FormControl>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                <Alert variant="soft" color="warning" sx={{ mt: 3 }}>
                  <Typography level="body-sm">
                    <strong>Important:</strong> This Workday URL cannot be
                    changed after you proceed. Please confirm it is correct
                    before continuing.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Help Box */}
          <Grid xs={12} md={4}>
            <Card
              variant="soft"
              color="primary"
              sx={{ height: "100%", position: "sticky", top: 20 }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoOutlinedIcon />
                    <Typography level="title-md">
                      Need Help Finding Your Workday URL?
                    </Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    <Typography level="title-sm">
                      Quick Setup - Just Paste Your URL!
                    </Typography>
                    <Typography level="body-sm">
                      Simply paste the homepage URL of the Workday tenant
                      associated with this Rooster environment.
                    </Typography>

                    <Box component="div" sx={{ pl: 0, m: 0 }}>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        <strong>To find your Workday URL:</strong>
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, mt: 0.5, mb: 2 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Log in to your Workday tenant.
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Copy the URL from your browser's address bar.
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            Paste it into the field on the left.
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            onClick={handleNext}
            size="lg"
            disabled={isLoading}
            startDecorator={isLoading && <CircularProgress size="sm" />}
          >
            {isLoading ? "Creating tenant..." : `Next: ${getNextButtonLabel()}`}
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export const workdayRoute = createRoute({
  getParentRoute: () => onboardingRootRoute,
  path: "/workday",
  component: WorkdayPage
});
